import { sumBy } from "lodash";
import {
  IPatientResponsibilityProcedure,
  IDefaultProcedure,
} from "types/Order/Order";
import { SpecialtyList } from "types/Provider/Provider";

export function parsePrice(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

// Medicare "No Maximum" OOP helpers
// Medicare has no out-of-pocket maximum, so we use a placeholder value of $1,000,000
// to make pricing calculations work. On the UI, we display "No maximum" instead.
export const MEDICARE_NO_LIMIT_OOP_CENTS = 100000000; // $1,000,000 in cents
export const MEDICARE_NO_LIMIT_OOP_DOLLARS = 1000000; // $1,000,000 in dollars

export const isNoMaximumOOP = (
  valueInCents: number | null | undefined
): boolean =>
  valueInCents != null && valueInCents >= MEDICARE_NO_LIMIT_OOP_CENTS;

export const isNoMaximumOOPDollars = (
  valueInDollars: number | null | undefined
): boolean =>
  valueInDollars != null && valueInDollars >= MEDICARE_NO_LIMIT_OOP_DOLLARS;

export function centsToDollars(cents: number): number {
  return cents / 100;
}

export function dollarsToCents(cents: number): number {
  return Math.round(cents * 100);
}

export function calculateDiscount(price: number, discount: number): number {
  return price - price * (discount / 100);
}

export function calculateProviderFinalPayment(
  price: number,
  clearFee: number | undefined
) {
  return clearFee ? price - price * (clearFee / 100) : 0;
}

export function calculateTax(price: number, tax: number) {
  return price + price * (tax / 100);
}

export function calculateDiscountPercent(
  totalPrice: number,
  suggestedPrice: number
): number {
  return (1 - suggestedPrice / totalPrice) * 100;
}

export function calculateClearCharge(price: number, fee: number): number {
  const priceWithoutCharge = fee ? price - price * (fee / 100) : 0;

  return price - priceWithoutCharge;
}

export const calculatePercentageOff = (
  realPrice: number,
  salePrice: number
): number => {
  return Math.round(((realPrice - salePrice) / realPrice) * 100);
};

export const sumOfFacilityProcedures = (
  procedures: Array<IPatientResponsibilityProcedure | IDefaultProcedure>
) =>
  sumBy(
    procedures.filter(
      (aProcedure: IPatientResponsibilityProcedure | IDefaultProcedure) =>
        "specialty" in aProcedure &&
        aProcedure.specialty === SpecialtyList.FACILITY
    ) || [],
    "amountInCents"
  );

export const sumOfProvidersProcedures = (
  procedures: Array<IPatientResponsibilityProcedure | IDefaultProcedure>
) =>
  sumBy(
    procedures.filter(
      (aProcedure: IPatientResponsibilityProcedure | IDefaultProcedure) =>
        "specialty" in aProcedure &&
        aProcedure.specialty !== SpecialtyList.FACILITY
    ) || [],
    "amountInCents"
  );

// Precalculated Allowables helpers

/**
 * Extract unique specialties from calculated data
 * @param data - ICalculatedData from API response
 * @returns Array of unique specialty names
 */
export function extractUniqueSpecialties(
  data: import("types/precalculatedAllowables.types").ICalculatedData
): string[] {
  const specialtiesSet = new Set<string>();

  data.cptCodes.forEach((cptCode) => {
    cptCode.providerProcedures.forEach((providerProcedure) => {
      specialtiesSet.add(providerProcedure.provider.specialty);
    });
  });

  return Array.from(specialtiesSet);
}

/**
 * Group CPT codes by provider specialty
 * For each specialty, includes only the providerProcedures from providers of that specialty
 * @param data - ICalculatedData from API response
 * @returns Map of specialty name to array of CPT codes with filtered providerProcedures
 */
export function groupCptCodesBySpecialty(
  data: import("types/precalculatedAllowables.types").ICalculatedData
): Map<string, import("types/precalculatedAllowables.types").ICptCodeData[]> {
  const specialtyMap = new Map<
    string,
    import("types/precalculatedAllowables.types").ICptCodeData[]
  >();

  // Extract unique specialties first
  const specialties = extractUniqueSpecialties(data);

  specialties.forEach((specialty) => {
    const cptCodesForSpecialty: import("types/precalculatedAllowables.types").ICptCodeData[] =
      [];

    data.cptCodes.forEach((cptCode) => {
      // Filter providerProcedures to only those from this specialty
      const filteredProcedures = cptCode.providerProcedures.filter(
        (pp) => pp.provider.specialty === specialty
      );

      // Only include this CPT code if it has procedures for this specialty
      if (filteredProcedures.length > 0) {
        cptCodesForSpecialty.push({
          ...cptCode,
          providerProcedures: filteredProcedures,
        });
      }
    });

    if (cptCodesForSpecialty.length > 0) {
      specialtyMap.set(specialty, cptCodesForSpecialty);
    }
  });

  return specialtyMap;
}

/**
 * Calculate total pricing by specialty
 * Sums priceInCents + implantCostInCents (treating null as 0) for all provider procedures
 * belonging to providers of each specialty
 * @param data - ICalculatedData from API response
 * @returns Map of specialty name to total pricing in cents
 */
export function calculateTotalPricingBySpecialty(
  data: import("types/precalculatedAllowables.types").ICalculatedData
): Map<string, number> {
  const pricingMap = new Map<string, number>();

  data.cptCodes.forEach((cptCode) => {
    cptCode.providerProcedures.forEach((providerProcedure) => {
      const { specialty } = providerProcedure.provider;
      const currentTotal = pricingMap.get(specialty) || 0;

      // Sum priceInCents + (implantCostInCents ?? 0)
      const procedureTotal =
        providerProcedure.priceInCents +
        (providerProcedure.implantCostInCents ?? 0);

      pricingMap.set(specialty, currentTotal + procedureTotal);
    });
  });

  return pricingMap;
}

/**
 * Sort specialties with priority ordering:
 * 1. FACILITY first (if present)
 * 2. PRIMARY_PROVIDER second (if present)
 * 3. All remaining specialties alphabetically
 * @param specialties - Array of specialty names
 * @param pricingMap - Map of specialty to total pricing in cents (unused, kept for backwards compatibility)
 * @returns Sorted array of specialty names
 */
export function sortSpecialtiesByPricing(
  specialties: string[],
  pricingMap: Map<string, number>
): string[] {
  return specialties.sort((a, b) => {
    // FACILITY always comes first
    if (a === SpecialtyList.FACILITY) return -1;
    if (b === SpecialtyList.FACILITY) return 1;

    // PRIMARY_PROVIDER always comes second
    if (a === SpecialtyList.PRIMARY_PROVIDER) return -1;
    if (b === SpecialtyList.PRIMARY_PROVIDER) return 1;

    // All other specialties sorted alphabetically
    return a.localeCompare(b);
  });
}
